import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

export default function Terms() {
    return (
        <div className="min-h-screen bg-white">
            <header className="px-6 md:px-12 lg:px-16 h-16 flex items-center justify-between border-b border-slate-200/60 sticky top-0 bg-white/90 backdrop-blur-xl z-50">
                <Link to="/" className="flex items-center gap-2.5" data-testid="terms-logo-link">
                    <div className="h-8 w-8 bg-brand text-white grid place-items-center font-heading font-bold tracking-tight rounded-sm">A</div>
                    <div className="leading-tight">
                        <div className="font-heading font-bold text-slate-900 text-base">Allied Pod</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono">Barbados</div>
                    </div>
                </Link>
                <Link to="/" data-testid="terms-back-link">
                    <Button variant="ghost" size="sm" className="text-slate-700">
                        <ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={1.75} /> Back
                    </Button>
                </Link>
            </header>
            <main className="px-6 md:px-12 lg:px-16 py-16 max-w-3xl mx-auto">
                <p className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-500 mb-3">Legal</p>
                <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-bold text-slate-900 leading-tight">
                    Flexible Office Pod Licence Agreement
                </h1>
                <p className="text-base text-slate-600 mt-4 leading-relaxed">
                    Short-Term Workspace Use Agreement between Allied Consulting and the User.
                    By registering, purchasing credits, or using the workspace, you confirm that you have read, understood, and agree to be bound by these terms.
                </p>

                <div className="mt-12 space-y-10 text-sm text-slate-700 leading-relaxed">

                    <Section title="Parties">
                        <p>This Flexible Office Pod Licence Agreement (<em>"Agreement"</em>) is made between:</p>
                        <p><strong>Allied Consulting</strong>, Registered Business No. 79491 in Barbados in compliance with the Registration of Business Names Act, Cap. 317 (<em>"Licensor", "we", "us"</em>);</p>
                        <p><strong>AND</strong> the User identified upon account creation (<em>"Licensee", "User", "you"</em>).</p>
                        <p>Whereas, Allied Consulting is prepared to engage the User for the use of the Short-Term Flexible Workspace in the nation of Barbados in accordance with the terms and conditions contained herein.</p>
                    </Section>

                    <Section title="1. Nature of Agreement">
                        <ul>
                            <li>1.1 This Agreement grants a revocable, non-exclusive licence to use a designated office space (<em>"Flexible Office Pod"</em>).</li>
                            <li>1.2 This Agreement does not create a tenancy, leasehold interest, or exclusive possession.</li>
                            <li>1.3 The Licensor retains full control and possession of the premises at all times.</li>
                        </ul>
                    </Section>

                    <Section title="2. Premises">
                        <ul>
                            <li>2.1 The workspace is located at: 1st Floor, One Welches, Welches, St. Thomas, Barbados (BB22025).</li>
                            <li>2.2 Access is controlled via building reception/front desk, and subject to security procedures.</li>
                            <li>2.3 No guarantee is made regarding uninterrupted access due to building operations or external factors.</li>
                        </ul>
                    </Section>

                    <Section title="3. Services Included">
                        <p>Subject to availability and reasonable use:</p>
                        <ul>
                            <li>High-speed WiFi</li>
                            <li>External monitor</li>
                            <li>Ergonomic desk and chair</li>
                            <li>Air-conditioned workspace</li>
                            <li>Additional power adapter access and use</li>
                            <li>Printing (up to 20 pages per booking)</li>
                            <li>Basic refreshments (water, tea, coffee)</li>
                            <li>Basic stationery</li>
                        </ul>
                        <p>3.1 Additional printing is charged at USD $0.15 per page.</p>
                    </Section>

                    <Section title="4. Booking & Credit System">
                        <ul>
                            <li>4.1 The system operates on a prepaid credit model:
                                <ul className="ml-5 mt-2">
                                    <li>1 Credit = Half Day (up to 4 hours)</li>
                                    <li>2 Credits = Full Day (up to 8 hours)</li>
                                </ul>
                            </li>
                            <li>4.2 Credits are valid for 45 days from purchase, after which they expire without refund.</li>
                            <li>4.3 Bookings:
                                <ul className="ml-5 mt-2">
                                    <li>Same-day bookings permitted (subject to availability)</li>
                                    <li>Maximum advance booking window: 14 days</li>
                                </ul>
                            </li>
                            <li>4.4 Bookings are confirmed only upon successful credit deduction.</li>
                        </ul>
                    </Section>

                    <Section title="5. Cancellation & No-Show Policy">
                        <ul>
                            <li>5.1 Cancellations must be made at least 24 hours prior to the booking start time.</li>
                            <li>5.2 If cancelled within the permitted window: Credit(s) will be reinstated.</li>
                            <li>5.3 If cancelled late or in the event of a no-show: Credits are forfeited.</li>
                            <li>5.4 One-time waiver: The first no-show may be waived at the Licensor's discretion. Subsequent no-shows will result in full credit forfeiture.</li>
                        </ul>
                    </Section>

                    <Section title="6. Time Use & Overrun Policy">
                        <ul>
                            <li>6.1 Users must vacate the workspace promptly at the end of their booking.</li>
                            <li>6.2 A grace period of 15–20 minutes is allowed.</li>
                            <li>6.3 Exceeding this grace period will result in immediate deduction of 1 additional credit.</li>
                            <li>6.4 Overnight use is strictly prohibited.</li>
                        </ul>
                    </Section>

                    <Section title="7. Acceptable Use">
                        <ul>
                            <li>7.1 The workspace may only be used for lawful professional purposes.</li>
                            <li>7.2 The following are strictly prohibited, including but not limited to:
                                <ul className="ml-5 mt-2">
                                    <li>Illegal activities of any kind</li>
                                    <li>Fraud, cybercrime, or unauthorized data access</li>
                                    <li>Harassment, abusive, or disruptive behavior</li>
                                    <li>Storage or handling of hazardous materials</li>
                                    <li>Activities causing excessive noise or disturbance</li>
                                    <li>Use of the space for residential or sleeping purposes</li>
                                </ul>
                            </li>
                            <li>7.3 No storage:
                                <ul className="ml-5 mt-2">
                                    <li>Users must remove all personal belongings at the end of each session.</li>
                                    <li>Items left behind may be removed or disposed of without liability during nightly cleaning.</li>
                                </ul>
                            </li>
                        </ul>
                    </Section>

                    <Section title="8. Damage & Loss">
                        <ul>
                            <li>8.1 The User is responsible for any damage to: furniture, equipment, fixtures, building property.</li>
                            <li>8.2 Liability includes full cost of repair or replacement.</li>
                            <li>8.3 The Licensor reserves the right to suspend or terminate account access until payment is made.</li>
                        </ul>
                    </Section>

                    <Section title="9. Access & Security">
                        <ul>
                            <li>9.1 Access is managed via building reception.</li>
                            <li>9.2 The Licensor does not guarantee security of personal belongings or continuous availability of access systems.</li>
                            <li>9.3 While the premises are monitored by CCTV and security personnel, the Licensee acknowledges and accepts use of the premises at the User's own risk.</li>
                        </ul>
                    </Section>

                    <Section title="10. No Monitoring Disclosure">
                        <ul>
                            <li>10.1 The Licensor does not provide internal CCTV monitoring within the office space.</li>
                            <li>10.2 Users are responsible for safeguarding their own property and information.</li>
                        </ul>
                    </Section>

                    <Section title="11. Confidentiality">
                        <ul>
                            <li>11.1 Both parties agree to mutual confidentiality.</li>
                            <li>11.2 Each party shall not disclose business information, client data, or documents/materials observed or accessed.</li>
                            <li>11.3 This obligation survives termination of this Agreement.</li>
                        </ul>
                    </Section>

                    <Section title="12. Third-Party Bookings">
                        <ul>
                            <li>12.1 If a booking is made on behalf of another individual, the account holder remains fully liable for all obligations, damages, and breaches.</li>
                        </ul>
                    </Section>

                    <Section title="13. Limitation of Liability">
                        <ul>
                            <li>13.1 To the maximum extent permitted by law, the Licensor shall not be liable for:
                                <ul className="ml-5 mt-2">
                                    <li>Loss of profits, revenue, or business opportunities</li>
                                    <li>Data loss or breaches</li>
                                    <li>Personal injury (except where legally unavoidable)</li>
                                    <li>Theft, loss, or damage to personal property</li>
                                </ul>
                            </li>
                            <li>13.2 Total liability is limited to the value of credits used for the booking in question.</li>
                        </ul>
                    </Section>

                    <Section title="14. Indemnity">
                        <ul>
                            <li>14.1 The User agrees to indemnify and hold harmless the Licensor against:
                                <ul className="ml-5 mt-2">
                                    <li>Claims arising from misuse of the space</li>
                                    <li>Breach of this Agreement</li>
                                    <li>Third-party claims related to User activities</li>
                                </ul>
                            </li>
                        </ul>
                    </Section>

                    <Section title="15. Insurance Disclaimer">
                        <ul>
                            <li>15.1 The Licensor does not maintain insurance coverage for User property or User liability.</li>
                            <li>15.2 Users are responsible for obtaining their own insurance if required.</li>
                        </ul>
                    </Section>

                    <Section title="16. Termination & Suspension">
                        <ul>
                            <li>16.1 The Licensor may suspend or terminate access immediately if terms are breached, payment obligations are not met, or misuse of the space occurs.</li>
                            <li>16.2 No refunds will be issued upon termination due to breach.</li>
                        </ul>
                    </Section>

                    <Section title="17. Force Majeure">
                        <ul>
                            <li>17.1 The Licensor is not liable for failure to perform due to natural disasters, utility outages, government actions, or events beyond reasonable control.</li>
                        </ul>
                    </Section>

                    <Section title="18. Governing Law & Disputes">
                        <ul>
                            <li>18.1 This Agreement is governed by the laws of Barbados.</li>
                            <li>18.2 Dispute resolution: First, good faith mediation; then, the courts of Barbados.</li>
                        </ul>
                    </Section>

                    <Section title="19. Modifications">
                        <ul>
                            <li>19.1 The Licensor may update this Agreement at any time.</li>
                            <li>19.2 Continued use after notice of the changes constitutes acceptance of updated terms.</li>
                        </ul>
                    </Section>

                    <Section title="20. Entire Agreement">
                        <ul>
                            <li>20.1 This document constitutes the entire agreement between the parties.</li>
                        </ul>
                    </Section>

                    <Section title="21. Acceptance">
                        <p>By registering, purchasing credits, or using the workspace, the User confirms that they:</p>
                        <ul>
                            <li>Have read and understood this Agreement</li>
                            <li>Agree to be bound by its terms</li>
                        </ul>
                    </Section>
                </div>

                <div className="mt-16 pt-8 border-t border-slate-200 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                    <p className="text-xs text-slate-500">© {new Date().getFullYear()} Allied Consulting Inc. (Barbados). Effective from account creation.</p>
                    <Link to="/register">
                        <Button className="bg-brand hover:bg-brand-hover text-white rounded-md" data-testid="terms-to-register-btn">
                            Create account & accept
                        </Button>
                    </Link>
                </div>
            </main>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section>
            <h2 className="font-heading text-xl sm:text-2xl tracking-tight font-bold text-slate-900 pb-2 border-b border-slate-200">{title}</h2>
            <div className="mt-4 space-y-3 [&_ul]:space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:leading-relaxed">{children}</div>
        </section>
    );
}
